import { Input as MUIInput, InputAdornment, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { Search as SearchIcon } from "@mui/icons-material";
import { makeStyles } from "@mui/styles";
import { useRef, memo, useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";

const useStyles = makeStyles((theme) => ({
	search: {
		background: theme.palette.light.main,
		borderRadius: "10px",
		position: "relative",
		padding: "5px 10px",
	},
}));

const Search = ({
	value: searchValue,
	width = "100%",
	onChange,
	results,
}) => {
	const theme = useTheme();
	const classes = useStyles();
	const inputRef = useRef(null);
	const searchContainerRef = useRef(null);
	const navigate = useNavigate();
	const location = useLocation();
	const [value, setValue] = useState(searchValue);
	const [dropdownVisible, setDropdownVisible] = useState(false);
	const [focusedIndex, setFocusedIndex] = useState(-1);

	// Reset focused index when results change
	useEffect(() => {
		setFocusedIndex(-1);
	}, [results]);

	useEffect(() => {
		setValue(searchValue);
	}, [searchValue]);

	// Hide dropdown and blur input only when the pathname changes (not just state/query params)
	useEffect(() => {
		setDropdownVisible(false); // Hide the dropdown
		inputRef.current?.blur(); // Ensure input loses focus
	}, [location.pathname]);

	// Handle keyboard shortcut (Ctrl+K or /) to focus search
	useEffect(() => {
		const handleGlobalKeyDown = (e) => {
			// Don't trigger if user is typing in another input
			if (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA" || e.target.isContentEditable) {
				return;
			}

			if ((e.ctrlKey && e.key === "k") || e.key === "/") {
				e.preventDefault();
				inputRef.current?.focus();
			}
		};

		window.addEventListener("keydown", handleGlobalKeyDown);
		return () => window.removeEventListener("keydown", handleGlobalKeyDown);
	}, []);

	// Handle clicks outside the search component
	useEffect(() => {
		const handleClickOutside = (event) => {
			if (searchContainerRef.current && !searchContainerRef.current.contains(event.target)) {
				setDropdownVisible(false);
				// We don't necessarily want to clear the input on click outside anymore,
				// as it might be annoying. But keeping it for now if that was the intended behavior.
				// However, the user might want to keep the search term but hide dropdown.
				// Let's keep existing behavior for consistency but it's often better to just hide.
				// setValue("");
				// const syntheticEvent = { target: { value: "" }, preventDefault() {}, stopPropagation() {} };
				// onChange(syntheticEvent);
			}
		};

		document.addEventListener("mousedown", handleClickOutside);

		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, [onChange]);

	// Helper to highlight matching text - uses safe string operations instead of dynamic regex
	const renderHighlightedName = (name, query) => {
		if (!query) return name;

		const parts = [];
		let remaining = name;
		let lowerName = name.toLowerCase();
		let lowerQuery = query.toLowerCase();
		let searchIndex = 0;

		while (searchIndex < name.length) {
			const matchIndex = lowerName.indexOf(lowerQuery, searchIndex);

			if (matchIndex === -1) {
				// No more matches, add the rest
				parts.push({
					text: remaining.substring(searchIndex - (name.length - remaining.length)),
					isMatch: false,
				});
				break;
			}

			// Add text before the match
			if (matchIndex > searchIndex) {
				parts.push({
					text: name.substring(searchIndex, matchIndex),
					isMatch: false,
				});
			}

			// Add the matched text
			parts.push({
				text: name.substring(matchIndex, matchIndex + query.length),
				isMatch: true,
			});

			searchIndex = matchIndex + query.length;
		}

		return (
			<span>
				{parts.map((part, i) => (
					part.isMatch ? (
						<strong key={i} style={{ color: theme.palette.secondary.main, fontWeight: "bold" }}>{part.text}</strong>
					) : (
						<span key={i}>{part.text}</span>
					)
				))}
			</span>
		);
	};

	// Handle navigation to the selected result
	const handleResultSelect = (result) => {
		// Clear the search input
		setValue("");
		setDropdownVisible(false);

		// Create a synthetic event to notify parent component
		const syntheticEvent = {
			target: { value: "" },
			preventDefault() {},
			stopPropagation() {},
		};
		onChange(syntheticEvent);

		switch (result.type) {
			case "product": {
				// Check if this is a sub-product with section information
				if (result.subProduct && result.section) {
					navigate("/products", {
						state: {
							selectedProduct: result.product.text,
							selectedSubProduct: result.subProduct,
							section: result.section,
							parentProduct: result.product,
						},
					});
				} else {
					navigate("/products", { state: { selectedProduct: result.name } });
				}

				break;
			}

			case "map": {
				navigate("/map", {
					state: {
						selectedProduct: result.name.replace(" Map", "").split(" (")[0],
						productValue: result.product?.value,
						specificProduct: result.specificProduct || null,
						parentProduct: result.specificProduct ? result.product?.text : null,
					},
				});
				break;
			}

			case "lab":
			case "screen":
			case "example": {
				navigate(result.link, { replace: true });
				break;
			}

			case "contaminant": {
				navigate("/contaminants", { state: { selectedContaminant: result.name } });
				break;
			}

			case "indicator": {
				// Navigate to Social Impact with pre-selected indicator
				navigate("/socialimpact", {
					state: {
						selectedIndicator: result.indicator,
						selectedCategory: result.category,
					},
				});
				break;
			}

			case "category": {
				// Navigate to Social Impact with pre-selected category
				navigate("/socialimpact", {
					state: {
						selectedCategory: result.category,
					},
				});
				break;
			}

			case "socialimpact-country": {
				// Navigate to Social Impact with pre-selected country
				navigate("/socialimpact", {
					state: {
						selectedCountry: result.country,
					},
				});
				break;
			}

			case "efsa-country": {
				// Navigate to Contaminants with pre-selected country
				navigate("/contaminants", {
					state: {
						selectedCountry: result.countryName,
					},
				});
				break;
			}

			case "disaster": {
				navigate("/disasters");
				break;
			}

			case "disaster-event": {
				navigate("/disasters", {
					state: { selectedEventType: result.eventTypeText },
				});
				break;
			}

			case "lca-indicator":
			case "env-category":
			case "social-indicator":
			case "social-category": {
				if (result.lab) {
					const isSocial = result.type === "social-indicator" || result.type === "social-category";
					navigate(result.link, {
						state: {
							openTab: "Life Cycle Assessment",
							lcaSubTab: isSocial ? "Social Analysis" : "Environmental Analysis",
							...(result.envCategory && { envCategory: result.envCategory }),
							...(result.socialCategory && { socialCategory: result.socialCategory }),
						},
					});
				} else {
					navigate("/socialimpact");
				}
				break;
			}

			default:// Do nothing
		}
	};

	// Handle keyboard navigation
	const handleKeyDown = (e) => {
		if (dropdownVisible && results.length > 0) {
			switch (e.key) {
				case "ArrowDown": {
					e.preventDefault();
					setFocusedIndex((prev) => (prev < results.length - 1 ? prev + 1 : prev));
					break;
				}

				case "ArrowUp": {
					e.preventDefault();
					setFocusedIndex((prev) => (prev > 0 ? prev - 1 : 0));
					break;
				}

				case "Enter": {
					e.preventDefault();
					if (focusedIndex >= 0) {
						handleResultSelect(results[focusedIndex]);
					}

					break;
				}

				case "Escape": {
					setDropdownVisible(false);
					setFocusedIndex(-1);
					break;
				}

				default: {
					break;
				}
			}
		}
	};

	return (
		<div
			ref={searchContainerRef}
			style={{ position: "relative", width }}
		>
			<MUIInput
				disableUnderline
				type="search"
				value={value}
				name="search"
				autoComplete="off"
				className={classes.search}
				sx={{ width }}
				inputRef={inputRef}
				startAdornment={(
					<InputAdornment
						sx={{
							position: "absolute",
							display: value ? "none" : "flex",
							marginLeft: "0px",
							cursor: "text",
							pointerEvents: "auto",
							zIndex: 1,
						}}
						position="start"
						onClick={() => inputRef.current?.focus()}
					>
						<SearchIcon />
						<Typography ml={1}>{"Search..."}</Typography>
					</InputAdornment>
				)}
				onChange={(e) => {
					setValue(e.target.value);
					onChange(e);
					setDropdownVisible(true);
				}}
				onFocus={() => {
					if (value) setDropdownVisible(true);
				}}
				onKeyDown={handleKeyDown}
			/>

			{/* SEARCH DROPDOWN */}
			{dropdownVisible && value && (
				<div
					role="listbox"
					style={{
						position: "absolute",
						top: "100%",
						left: 0,
						width: "100%",
						background: "white",
						boxShadow: `0px 8px 16px ${theme.palette.dark.main}26`,
						borderRadius: "8px",
						zIndex: 1000,
						marginTop: "4px",
						maxHeight: "350px",
						overflowY: "auto",
						border: `1px solid ${theme.palette.grey.main}`,
					}}
				>
					{results.length > 0 ? (
						results.map((result, index) => (
							<div
								key={index}
								role="option"
								aria-selected={index === focusedIndex}
								tabIndex={0}
								style={{
									padding: "12px 16px",
									cursor: "pointer",
									borderBottom: index === results.length - 1 ? "none" : `1px solid ${theme.palette.light.main}`,
									color: theme.palette.dark.main,
									display: "flex",
									flexDirection: "column",
									backgroundColor: index === focusedIndex ? `${theme.palette.secondary.main}1A` : "white",
									transition: "background-color 0.2s ease",
								}}
								onClick={() => handleResultSelect(result)}
								onMouseEnter={() => setFocusedIndex(index)}
								onKeyDown={(e) => {
									if (e.key === "Enter" || e.key === " ") {
										e.preventDefault();
										handleResultSelect(result);
									}
								}}
							>
								<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
									<span style={{ fontWeight: 500 }}>{renderHighlightedName(result.name, value)}</span>
									<span style={{
										fontSize: "0.7rem",
										padding: "2px 6px",
										borderRadius: "4px",
										background: theme.palette.light.main,
										color: theme.palette.greyDark.main,
										fontWeight: "bold",
										textTransform: "uppercase",
									}}
									>
										{result.lab?.title || (result.link === "/socialimpact" ? "social impact" : result.type)}
									</span>
								</div>
								{result.product?.description && (
									<Typography variant="caption" sx={{ color: theme.palette.greyDark.main, mt: 0.5, display: "-webkit-box", WebkitLineClamp: 1, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
										{result.product.description}
									</Typography>
								)}
							</div>
						))
					) : (
						<div style={{ padding: "20px", textAlign: "center", color: theme.palette.greyDark.main }}>
							<Typography variant="body2">
								{"No results found for \""}
								{value}
								{"\""}
							</Typography>
						</div>
					)}
				</div>
			)}
		</div>
	);
};

export default memo(Search);
